<template>
    <div class="exam-video-slot border rounded p-2 mb-2">
        <div class="row align-items-start">
            <div class="col-12 col-md-3 mb-2">
                <label class="mb-0"><b>{{ $t('Video') }} {{ slot }}</b></label>
            </div>
            <div class="col-12 col-md-5 mb-2">
                <select class="form-control form-control-sm" v-model="titleChoice" :disabled="disabled"
                    @change="onTitleChange">
                    <option value="">{{ $t('Select title') }}</option>
                    <option v-for="opt in titleOptions" :key="opt" :value="opt">{{ opt }}</option>
                </select>
                <input v-if="titleChoice === 'Custom'" type="text" class="form-control form-control-sm mt-1"
                    v-model="customTitle" :placeholder="$t('Enter custom title')" :disabled="disabled"
                    @input="onTitleChangeDebounced" />
            </div>
            <div class="col-12 col-md-4 mb-2">
                <!-- No video yet: upload control -->
                <template v-if="!url">
                    <input v-if="!disabled" ref="file" type="file" accept="video/*" class="form-control-file"
                        :disabled="busy" @change="onFileChange" />
                    <div v-if="busy" class="mt-1">
                        <small class="text-muted d-block">{{ status }}<span v-if="progress"> {{ progress }}%</span></small>
                        <div class="progress" style="height: 5px;">
                            <div class="progress-bar" role="progressbar" :style="{ width: (progress || 8) + '%' }"></div>
                        </div>
                    </div>
                    <small class="text-muted d-block mt-1">{{ $t('Max 30 seconds and 40 MB. Videos over 2 MB are compressed in your browser.') }}</small>
                </template>
                <!-- Video present: actions -->
                <template v-else>
                    <a :href="url" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary mr-1" :title="$t('View')">
                        <app-icon name="play" /> {{ $t('View') }}
                    </a>
                    <a :href="url" download class="btn btn-sm btn-outline-secondary mr-1" :title="$t('Download')">
                        <app-icon name="download" />
                    </a>
                    <button v-if="!disabled" type="button" class="btn btn-sm btn-outline-danger" :title="$t('Delete')" @click="remove">
                        <app-icon name="trash-2" />
                    </button>
                </template>
                <small v-if="error" class="text-danger d-block mt-1">{{ error }}</small>
            </div>
        </div>
    </div>
</template>

<script>
import { axiosPost, axiosDelete } from "../../../../../common/Helper/AxiosHelper";
import { EXAM_VIDEO_UPLOAD, EXAM_VIDEO_DELETE, EXAM_VIDEO_TITLE } from "../../../../Config/ApiUrl-CP";
import { compressVideo } from "../../../../../common/Helper/FfmpegHelper";

export default {
    name: "exam-video-slot",
    props: {
        customerId: { required: true },
        slot: { type: Number, required: true },
        url: { default: null },
        title: { type: String, default: "" },
        disabled: { type: Boolean, default: false },
    },
    data() {
        return {
            titleOptions: ["Gait anterior - posterior", "Gait medio-lateral", "Custom"],
            titleChoice: "",
            customTitle: "",
            busy: false,
            progress: 0,
            status: "",
            error: "",
        };
    },
    computed: {
        resolvedTitle() {
            return this.titleChoice === "Custom" ? (this.customTitle || "").trim() : this.titleChoice;
        },
    },
    watch: {
        // The saved title arrives asynchronously (and again after reload); reflect it in the
        // controls so it displays correctly.
        title(val) {
            this.applyTitle(val);
        },
    },
    mounted() {
        this.applyTitle(this.title);
    },
    methods: {
        applyTitle(saved) {
            saved = (saved || "").trim();
            if (saved === this.resolvedTitle) return; // already in sync — avoids emit/save loops
            if (saved && this.titleOptions.includes(saved)) {
                this.titleChoice = saved;
                this.customTitle = "";
            } else if (saved) {
                this.titleChoice = "Custom";
                this.customTitle = saved;
            } else {
                this.titleChoice = "";
                this.customTitle = "";
            }
        },
        onTitleChange() {
            this.$emit("title-change", this.resolvedTitle);
            this.persistTitle();
        },
        onTitleChangeDebounced() {
            this.$emit("title-change", this.resolvedTitle);
            clearTimeout(this._titleTimer);
            this._titleTimer = setTimeout(() => this.persistTitle(), 700);
        },
        persistTitle() {
            // Save the title immediately (like the video), so it doesn't depend on a separate Save.
            axiosPost(EXAM_VIDEO_TITLE + this.customerId, { slot: this.slot, title: this.resolvedTitle })
                .catch(() => { /* the main Examination Save still carries the title as a fallback */ });
        },
        getDuration(file) {
            return new Promise((resolve, reject) => {
                const v = document.createElement("video");
                v.preload = "metadata";
                v.onloadedmetadata = () => { const d = v.duration; URL.revokeObjectURL(v.src); resolve(d); };
                v.onerror = () => { URL.revokeObjectURL(v.src); reject(); };
                v.src = URL.createObjectURL(file);
            });
        },
        resetInput() {
            if (this.$refs.file) this.$refs.file.value = "";
        },
        async onFileChange(e) {
            const file = e.target.files && e.target.files[0];
            if (!file) return;
            this.error = "";

            // Original-file cap: 40 MB.
            if (file.size > 40 * 1024 * 1024) {
                this.error = this.$t("Video is over 40 MB. Please choose a smaller file or record at lower quality.");
                this.resetInput();
                return;
            }

            // Duration cap: 30 seconds.
            let duration = null;
            try { duration = await this.getDuration(file); } catch (err) { duration = null; }
            if (duration !== null && duration > 30.5) {
                this.error = this.$t("Video is") + " " + Math.round(duration) + "s. " + this.$t("Please upload a clip of 30 seconds or less.");
                this.resetInput();
                return;
            }

            let blob = file;
            this.busy = true;
            this.progress = 0;

            // Compress if over 2 MB (small files upload as-is).
            if (file.size > 2 * 1024 * 1024) {
                this.status = this.$t("Compressing… (first time loads the compressor)");
                try {
                    blob = await compressVideo(file, (r) => { this.progress = Math.round(r * 100); });
                } catch (err) {
                    this.busy = false;
                    // eslint-disable-next-line no-console
                    console.error("Video compression failed:", err);
                    const detail = err && err.message ? (" (" + err.message + ")") : "";
                    this.error = this.$t("Could not compress this video.") + detail;
                    this.resetInput();
                    return;
                }
            }

            // Upload.
            this.status = this.$t("Uploading…");
            this.progress = 0;
            const fd = new FormData();
            fd.append("slot", this.slot);
            fd.append("video", blob, "video.mp4");
            fd.append("title", this.resolvedTitle);
            try {
                const res = await axiosPost(EXAM_VIDEO_UPLOAD + this.customerId, fd);
                this.$emit("update:url", (res.data && res.data.url) || null);
                this.$toastr.s(this.$t("Video uploaded."));
            } catch (err) {
                const msg = (err.response && err.response.data && err.response.data.message) || this.$t("Upload failed.");
                this.error = msg;
            } finally {
                this.busy = false;
                this.progress = 0;
                this.status = "";
                this.resetInput();
            }
        },
        remove() {
            if (!confirm(this.$t("Delete this video?"))) return;
            axiosDelete(EXAM_VIDEO_DELETE + this.customerId + "/" + this.slot)
                .then(() => {
                    this.$emit("update:url", null);
                    this.$toastr.s(this.$t("Video deleted."));
                })
                .catch(() => this.$toastr.e(this.$t("Could not delete the video.")));
        },
    },
};
</script>

<style scoped>
.exam-video-slot { background: #fbfcfe; }
</style>
